import { useEffect, useMemo, useRef, type ReactNode } from 'react'
import { connect } from 'react-redux'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import styled from 'styled-components'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import NavigableOfferContent from 'components/OfferPage/NavigableOfferContent/NavigableOfferContent'
import PropertyNavigableOfferContent from 'components/OfferPage/NavigableOfferContent/PropertyNavigableOfferContent'
import config from 'constants/config'
import {
  OFFER_TYPE_TOUR,
  OFFER_TYPE_TOUR_V2,
  OFFER_TYPE_CRUISE_SLUG,
  OFFER_TYPE_TOUR_SLUG,
  OFFER_TYPE_HOTEL,
  OFFER_TYPE_ALWAYS_ON,
  OFFER_TYPE_BED_BANK,
  PRODUCT_TYPE_ULTRALUX,
} from 'constants/offer'
import { TOP_YMAL } from 'constants/fetchOfferStrategy'
import { fetchAlternativeOffers, fetchOfferById, fetchTraderInformation, offerViewed } from 'actions/OfferActions'
import { offerProductDetailView } from 'actions/AnalyticsActions'
import { useLocation, useParams } from 'react-router-dom'
import Error404PageWithOffers from 'components/Pages/Error404PageWithOffers/Error404PageWithOffers'
import Error500Page from 'components/Pages/Error500Page'
import Pane from 'components/Common/Pane'
import LayoutContainer from 'components/Common/LayoutContainer'
import OfferContentLoader from 'components/OfferPage/OfferContentLoader'
import { BottomMobileRelatedTravelItems } from 'components/OfferPage/RelatedTravelItems'
import OfferPageHeadData from 'components/OfferPage/OfferPageHeadData'
import Heading from 'LuxKit/Typography/Heading'
import RedirectWithStatus from 'components/Common/RedirectWithStatus'
import { fetchBedbankOfferById } from 'actions/BedbankOfferActions'
import {
  getPropertyBedbankOffer,
  getPropertyAlternateURL,
  getPropertyBestAlternateURL,
  getPropertyAlternateOfferIds,
} from 'selectors/offerPage/offerPageSelectors'
import { hasOrganicReferrer } from 'selectors/configSelectors'
import { scheduleIsCurrent } from 'lib/offer/scheduleStatusUtils'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { ENABLED_OFFER_TYPES_RECENTLY_VIEWED } from 'lib/offer/highIntentOffers'
import ErrorBoundary from 'components/Common/ErrorBoundary'
import FlashTourContent from './FlashTour/FlashTourContent'
import BundleAndSaveContent from './BundleAndSave/BundleAndSaveContent'
import { fireEcommerceGTMEvent } from 'api/googleTagManager'
import { accommodationOfferClicked } from 'analytics/eventDefinitions'
import offerPageURL from 'lib/offer/offerPageURL'
import { isBundleOffer, isCruiseV1Offer, isLpcHotelOffer, isTourV1Offer, isVillaOffer } from 'lib/offer/offerTypes'
import AnalyticsPageContext, { AnalyticsPage } from 'contexts/Analytics/analyticsPageContext'
import OfferRecommendations from 'components/Common/OfferRecommendations/OfferRecommendations'
import SimilarOfferRecommendationsExperiment from 'components/Common/OfferRecommendations/SimilarOfferRecommendationsExperiment'
import VillasPageContent from './Villas/VillasPageContent'
import CruiseRecommendations from 'components/Common/OfferRecommendations/CruiseRecommendations'
import HttpStatus from 'components/Common/HttpStatus'
import { usePlaceByName } from 'hooks/usePlace'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { usePageActivityTracking } from 'analytics/snowplow/helpers/useActivityTracking'
import { LuxPlusLabelProvider } from 'contexts/LuxPlusLabelContext'
import LuxPlusHiddenOfferOfferContent from 'luxPlus/components/HiddenOffer/LuxPlusHiddenOfferOfferContent'
import { getIsLuxPlusHiddenOffer } from 'luxPlus/selectors/luxPlusHiddenOffer'
import HowItWorksItems from 'components/Common/HowItWorks/HowItWorksItems'
import Group from 'components/utils/Group'
import useProperty from 'hooks/Offers/useProperty'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import SoftOfferPageWalledGarden from 'components/Common/SoftOfferPageWalledGarden'
import SiteTakeoverOfferPageBanner from 'takeovers/utils/components/SiteTakeoverOfferPageBanner'
import useCruiseSearchFacets, { FACET_TYPES } from 'hooks/Cruise/useCruiseSearchFacets'
import FlashHotelContainer from './FlashHotel/FlashHotelContainer'
import PropertyContainer from './Property/PropertyContainer'
import Sheet from 'components/utils/Sheet'
import useOffer from 'hooks/Offers/useOffer'
import useOfferList from 'hooks/Offers/useOfferList'
import { HOMES_AND_VILLAS_FILTERS } from 'constants/offerList'
import { useShouldShowBanner } from 'takeovers/utils/hooks/useShouldShowBanner'
import { getCharterBundleRoute, getShouldBlockMaldivesCharterOffer } from 'selectors/offerPage/initialOfferPageStateSelectors'
import { OptimizelyFeatureFlags } from 'constants/featureFlags'
import { useFunnelOptimiserExperimentTrigger } from 'checkout/hooks/useFunnelConfig'
import { shouldRedirectToCanonicalOfferUrl } from './offerPageCanonicalRedirect'
import OfferPageAssistantRailLayout from 'tripPlanner/assistant/surfaces/offerPage/OfferPageAssistantRailLayout'
import useFeatureFlag from 'hooks/Experiments/useFeatureFlag'

const OfferPageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  box-sizing: border-box;
`

const ObservableWrapper = styled.div`
  padding-top: 1px;
  margin-top: ${rem(24)};
`

const StyledPane = styled(Pane)`
  position: relative;
`

interface OwnProps {
  locationHash: string
  offerId: string
  offerSlug?: string
  propertyPageOfferId?: string
}

interface Props extends OwnProps {
  offerId: string
  traderInformation?: App.TraderInformation
  regionCode: string
  headlessMode: boolean
  lereOfferStateAvailable: boolean
  lereOfferCheckIn: string | undefined
  lereOfferCheckOut: string | undefined
  lereOfferRooms: Array<App.Occupants> | undefined
  propertyAlternateOfferIds?: App.AlternativeOffers
  propertyBedbankOffer?: App.BedbankOffer
  propertyAlternateUrl?: string
  propertyBestAlternateUrl?: string
  canDoSoldoutRedirect: boolean
  relatedTravelItems: App.OfferState['relatedTravelItems']
  currency: string
  isSpoofed: boolean
  utmMedium: string | undefined
}

const TOUR_HOLIDAY_TYPES = [OFFER_TYPE_TOUR_SLUG, OFFER_TYPE_CRUISE_SLUG]

type RecommendationFiltersWithRegion = App.OfferListFilters & {
  region: string
}

function generateFilters(
  offer: App.BundleOffer | App.HotelOffer | App.VillaOffer | App.TourOffer,
  strategyApplied = '',
): App.OfferListFilters {
  const filters: App.OfferListFilters = {}
  if (offer.holidayTypes?.length) {
    filters.holidayTypes = offer.holidayTypes
  }
  if (offer.locations?.length) {
    filters.locations = offer.locations
  }
  if (strategyApplied) {
    filters.strategyApplied = strategyApplied
  }
  if (offer.type === OFFER_TYPE_TOUR) {
    filters.holidayTypes = TOUR_HOLIDAY_TYPES
  }
  return filters
}

function getHowItWorksItems(offerType: App.OfferType | undefined) {
  if (offerType === OFFER_TYPE_TOUR) {
    return config.offerList.howItWorks[OFFER_TYPE_TOUR]
  }

  if (offerType === OFFER_TYPE_HOTEL) {
    return config.offerList.howItWorks[OFFER_TYPE_HOTEL]
  }

  return undefined
}

function OfferPage(props: Props) {
  const intl = useIntl()
  const {
    offerId,
    locationHash,
    offerSlug,
    headlessMode,
    lereOfferStateAvailable,
    lereOfferCheckIn,
    lereOfferCheckOut,
    lereOfferRooms,
    propertyAlternateOfferIds,
    propertyBedbankOffer,
    propertyAlternateUrl,
    propertyBestAlternateUrl,
    canDoSoldoutRedirect,
    relatedTravelItems,
    regionCode,
    traderInformation,
    propertyPageOfferId,
    currency,
    isSpoofed,
    utmMedium,
  } = props

  useFunnelOptimiserExperimentTrigger()

  const bypassRegionFilter = useAppSelector(state => !!state.marketing.bypassRegionSettingId && !state.marketing.bypassRegionExclusionOfferIds.includes(offerId))
  const [offer, _, offerError] = useOffer<App.BundleOffer | App.HotelOffer | App.VillaOffer | App.TourOffer>(offerId, { bypassRegionFilter: bypassRegionFilter })
  const propertyId = offer?.property?.id
  const dataLoaded = useRef(false)
  const dispatch = useAppDispatch()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const isHiddenOffer = useAppSelector(state => getIsLuxPlusHiddenOffer(state, offer))
  const charterBundleRoute = useAppSelector(state => getCharterBundleRoute(state, offer))
  const shouldBlockMaldivesCharterOffer = useAppSelector(state => getShouldBlockMaldivesCharterOffer(state, offer))
  const lereOfferPageRecommendationsEnabled: boolean = useFeatureFlag(OptimizelyFeatureFlags.lereOfferPageRecommendationsEnabled)

  const shouldUsePropertyPage = offer?.type === OFFER_TYPE_ALWAYS_ON
  const [propertyMapping] = usePropertyOfferMapping(OFFER_TYPE_ALWAYS_ON, offer?.id, {
    disabled: !shouldUsePropertyPage,
  })
  const [property] = useProperty(propertyMapping?.propertyId, { disabled: !shouldUsePropertyPage })
  const isPropertyPageReady = !!propertyMapping && !!property

  useEffect(() => {
    if (offer) {
      // we use dataLoaded ref to prevent multiple analytics pushes
      fireEcommerceGTMEvent(accommodationOfferClicked(offer, currency))
      dataLoaded.current = true
    }
  }, [offer, currency])

  usePageActivityTracking()

  useEffect(() => {
    if (offerId) {
      dispatch(fetchOfferById(offerId, { bypassRegionFilter: bypassRegionFilter }))
    } else if (propertyPageOfferId) {
      dispatch(fetchOfferById(propertyPageOfferId, { bypassRegionFilter: bypassRegionFilter }))
    }
  }, [dispatch, offerId, propertyPageOfferId, bypassRegionFilter])

  useEffect(() => {
    if (offer && config.TRADER_INFORMATION_REGIONS && isEnabledForFeature(config.TRADER_INFORMATION_REGIONS, regionCode)) {
      dispatch(fetchTraderInformation(offer.id, regionCode))
    }
  }, [dispatch, offer?.id, regionCode]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (offer) {
      dispatch(offerProductDetailView(offer))
      window.dataLayer?.push({ offerType: offer.type })
    }
  }, [offer?.id, utmMedium]) // oxlint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (propertyId && !propertyAlternateOfferIds) {
      dispatch(fetchAlternativeOffers(propertyId, 'le'))
    } else if (!!propertyAlternateOfferIds?.bedbank?.length && !propertyBedbankOffer) {
      dispatch(fetchBedbankOfferById(propertyAlternateOfferIds.bedbank[0]))
    }
  }, [propertyId, propertyAlternateOfferIds, propertyBedbankOffer, dispatch])

  useEffect(() => {
    if (!!offer && ENABLED_OFFER_TYPES_RECENTLY_VIEWED.has(offer.type)) {
      dispatch(offerViewed(offer.id, offer.type, false))
    }
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [offer?.id, dispatch])

  const offerRecommendationsFilters = useMemo(() => {
    return offer ? generateFilters(offer, TOP_YMAL) : {}
  }, [offer])

  const recommendationFilters: App.OfferListFilters = useMemo(() => {
    const retFilters: RecommendationFiltersWithRegion = {
      ...offerRecommendationsFilters,
      checkIn: lereOfferCheckIn,
      checkOut: lereOfferCheckOut,
      rooms: lereOfferRooms,
      lere: 'graph',
      region: regionCode,
      ...(offer?.id && { baseOfferId: offer.id }),
    }
    if ((config.BRAND === 'scoopontravel' || (!lereOfferPageRecommendationsEnabled && config.BRAND === 'luxuryescapes')) && propertyId) {
      retFilters.propertyId = `le:${propertyId}`
      retFilters.locations = undefined
      retFilters.strategyApplied = undefined
      retFilters.holidayTypes = undefined
      retFilters.offerTypes = undefined
      retFilters.baseOfferId = undefined
      retFilters.searchNearby = true
      retFilters.lere = undefined
      retFilters.isStream = true
    }
    return retFilters
  }, [lereOfferCheckIn, lereOfferCheckOut, lereOfferRooms, offer, offerRecommendationsFilters, propertyId, regionCode, lereOfferPageRecommendationsEnabled])

  const cruiseLocation = useMemo(() => {
    if (isCruiseV1Offer(offer)) {
      return offer.startLocation ?? [offer.locationHeading, offer.locationSubheading].filter(t => t).join(', ')
    }
  }, [offer])

  const cruisePlace = usePlaceByName(cruiseLocation)
  const shouldShowBanner = useShouldShowBanner({ location: 'offer', vertical: 'hotel', offer: isTourV1Offer(offer) ? undefined : offer })

  const [cruiseFacetsV2] = useCruiseSearchFacets({
    facetTypes: [FACET_TYPES.CRUISE_LINES],
    offerSource: 'cruises',
  })
  const isCruise = offer?.holidayTypes?.includes('Cruises')
  const recommendationFiltersCruise = useMemo<App.OfferListFilters>(() => {
    if (isCruise && offer) {
      const cruiseLine = cruiseFacetsV2.cruiseLines.find(({ label }) =>
        offer.vendorName && label.includes(offer.vendorName.split(' ')[0]) ||
        offer.property && label.includes(offer.property?.name.split(' ')[0]))

      return {
        offerTypes: ['cruise'],
        departurePlaceId: cruisePlace?.id,
        cruiseLines: cruiseLine ? [cruiseLine.slug] : undefined,
      }
    }
    return {}
  }, [cruiseFacetsV2.cruiseLines, cruisePlace?.id, isCruise, offer])

  const offerCountryPlace = usePlaceByName(offer?.property?.geoData?.country)

  const villaRecommendationsList = useOfferList({
    ...HOMES_AND_VILLAS_FILTERS,
    holidayTypes: offer?.holidayTypes,
    destinationId: offerCountryPlace?.id,
    scheduleVisibilities: ['listVisible'],
    isStream: true,
    limit: 8,
  }, {
    disabled: offer?.type !== 'rental' || !offerCountryPlace?.id,
    minRequiredOffers: 8,
  })

  if (offerError) {
    if (offerError.status === 404) {
      return <HttpStatus code={404}>
        <Error404PageWithOffers />
      </HttpStatus>
    } else {
      return <HttpStatus code={500}>
        <Error500Page error={offerError} />
      </HttpStatus>
    }
  }

  if ((shouldUsePropertyPage && !isPropertyPageReady) || !offer) {
    return <OfferContentLoader />
  }

  // Block Maldives charter flash offers when feature flag is disabled
  if (shouldBlockMaldivesCharterOffer) {
    return <HttpStatus code={404}>
      <Error404PageWithOffers />
    </HttpStatus>
  }

  if (isHiddenOffer) {
    return (
      <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
        <OfferPageHeadData offer={offer} isLuxPlusHiddenOffer />
        <LuxPlusHiddenOfferOfferContent offer={offer} />
      </AnalyticsPageContext.Provider>
    )
  }

  const isTourOffer = [OFFER_TYPE_TOUR, OFFER_TYPE_TOUR_V2].includes(offer.parentType)
  const isVilla = offer.type === 'rental'
  const breadCrumbItems = relatedTravelItems[offer?.id] || []
  const isBundleAndSave = isBundleOffer(offer)

  const {
    id,
    onlinePurchaseSchedule,
    type,
  } = offer
  const similarOffersTitle = offer.property?.name
    ? intl.formatMessage(
        {
          id: '9VU-fbcqdK87hNFh0tLz',
          defaultMessage: 'Similar to {hotelName}',
          description: 'Title for similar offers section on the offer listing or detail page',
        },
        { hotelName: offer.property.name },
      )
    : intl.formatMessage({
        id: 'nJUaZcgPjgC99vyMblPW',
        defaultMessage: 'Similar offers',
        description: 'Fallback title for the similar offers section when the property name is unavailable',
      })

  let howItWorksItems = getHowItWorksItems(type)

  // Remove BNBL item if it's a charter bundle offer
  if (!!charterBundleRoute && howItWorksItems) {
    howItWorksItems = howItWorksItems.filter(item => item.slug !== 'buy-now-book-later')
  }

  const availableForPurchase = scheduleIsCurrent(onlinePurchaseSchedule)
  if (shouldRedirectToCanonicalOfferUrl(offer, offerSlug, propertyPageOfferId)) {
    return <RedirectWithStatus code={301} to={`/${regionCode.toLowerCase()}${offerPageURL(offer)}`} />
  }

  if (offer.type !== 'hotel' && propertyBestAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyBestAlternateUrl} />
  }

  if (!availableForPurchase && canDoSoldoutRedirect && propertyAlternateUrl) {
    return <RedirectWithStatus code={301} to={propertyAlternateUrl} />
  }

  let offerPageContent: ReactNode = null
  let rendersSppStructuredData = false

  if (offer.type === OFFER_TYPE_HOTEL) {
    offerPageContent = <FlashHotelContainer offer={offer} key={offer.id} />
  } else if (offer.type === OFFER_TYPE_ALWAYS_ON && isLpcHotelOffer(offer) && property) {
    rendersSppStructuredData = true
    offerPageContent = (
      <PropertyContainer
        property={property}
        offer={offer}
        key={offer.id}
      />
    )
  } else if (isBundleAndSave) {
    offerPageContent = <BundleAndSaveContent offer={offer} key={offer.id} />
  } else if (isTourV1Offer(offer)) {
    offerPageContent = <FlashTourContent offer={offer} key={offer.id} />
  } else if (isVillaOffer(offer)) {
    offerPageContent = <VillasPageContent offer={offer} key={offer.id} />
  }

  return <AnalyticsPageContext.Provider value={AnalyticsPage.offerPage}>
    <OfferPageHeadData offer={offer} skipHotelStructuredData={rendersSppStructuredData} />
    <OfferPageWrapper className="OfferPage">
      {shouldShowBanner && <SiteTakeoverOfferPageBanner />}
      <OfferPageAssistantRailLayout headlessMode={headlessMode}>
        <LuxPlusLabelProvider page="offer-page">
          {offerPageContent}
        </LuxPlusLabelProvider>
        <SoftOfferPageWalledGarden bypassWall={[OFFER_TYPE_HOTEL, OFFER_TYPE_BED_BANK, OFFER_TYPE_ALWAYS_ON].includes(offer.type)}>
          <ObservableWrapper>
            {!isVilla && <StyledPane>
              {!isPropertyPageReady && <NavigableOfferContent
                offerDetails={offer}
                traderInformation={traderInformation}
                hash={locationHash}
                mobileHotelOfferPageTest={isMobileScreen}
                charterBundleRoute={charterBundleRoute}
              />}
              {isPropertyPageReady && <PropertyNavigableOfferContent
                offer={offer}
                property={property}
                propertyOfferMapping={propertyMapping}
                traderInformation={traderInformation}
                hash={locationHash}
              />}
              {howItWorksItems && !offer.bundledWithFlightsOnly && !offer.disableBestPriceGuarantee && offer.productType !== PRODUCT_TYPE_ULTRALUX && <Sheet padding="40 0">
                <LayoutContainer>
                  <Pane border="light-foreground" borderRadius="S" padding="40 24">
                    <Group direction="vertical" gap={32}>
                      <Heading variant="heading3" align="center">
                        {intl.formatMessage({
                          id: '2YoCLvI_Wc0RZR_r6q11',
                          defaultMessage: 'Why you should book with us',
                          description: 'Section heading for why book with us section on the offer detail page',
                        })}
                      </Heading>
                      <HowItWorksItems items={howItWorksItems} />
                    </Group>
                  </Pane>
                </LayoutContainer>
              </Sheet>}
            </StyledPane>}
          </ObservableWrapper>
          <ErrorBoundary fallback={null}>
            {isVilla && <OfferRecommendations
              title={similarOffersTitle}
              offerId={id}
              trackingDescription="Offer LE"
              overrideOfferList={villaRecommendationsList}
            />}
            {!isCruise && !isVilla && !isBundleAndSave && !isTourOffer && !isSpoofed && lereOfferStateAvailable &&
              <SimilarOfferRecommendationsExperiment
                title={similarOffersTitle}
                filters={recommendationFilters}
                offerId={id}
                trackingDescription="Offer LE"
                isSpoofed={isSpoofed}
              />}
          </ErrorBoundary>
          <ErrorBoundary fallback={null}>
            {isTourV1Offer(offer) && recommendationFiltersCruise.departurePlaceId && !isSpoofed &&
              <CruiseRecommendations
                filters={recommendationFiltersCruise}
                offer={offer}
                cruiseLineName={offer.vendorName}
                departurePortName={offer.startLocation}
              />}
          </ErrorBoundary>
          {!headlessMode &&
            <BottomMobileRelatedTravelItems offer={offer} items={breadCrumbItems} />}
        </SoftOfferPageWalledGarden>
      </OfferPageAssistantRailLayout>
    </OfferPageWrapper>
  </AnalyticsPageContext.Provider>
}

function mapStateToProps(state: App.State, ownProps: OwnProps) {
  const offerId = ownProps.propertyPageOfferId || ownProps.offerId
  const offerSharedState = state.recommendations.offerSharedState[offerId]

  return {
    offerId,
    regionCode: state.geo.currentRegionCode,
    canDoSoldoutRedirect: IS_SSR && hasOrganicReferrer(state),
    traderInformation: state.offer.traderInformation[offerId]?.data,
    headlessMode: state.system.headlessMode,
    propertyAlternateOfferIds: getPropertyAlternateOfferIds(state, offerId),
    propertyBedbankOffer: getPropertyBedbankOffer(state, offerId),
    propertyAlternateUrl: getPropertyAlternateURL(state, offerId),
    propertyBestAlternateUrl: IS_SSR && getPropertyBestAlternateURL(state, offerId),
    relatedTravelItems: state.offer.relatedTravelItems,
    lereOfferStateAvailable: !!offerSharedState,
    lereOfferCheckIn: offerSharedState?.checkIn,
    lereOfferCheckOut: offerSharedState?.checkOut,
    lereOfferRooms: offerSharedState?.rooms,
    currency: state.geo.currentCurrency,
    isSpoofed: state.auth.account.isSpoofed,
    utmMedium: state.utm.utmMedium,
  }
}

const ConnectedOfferPage = connect(mapStateToProps)(OfferPage)

function OfferPageRoute(props: Pick<OwnProps, 'propertyPageOfferId'>) {
  const { offerSlug, offerId } = useParams<'offerSlug' | 'offerId'>()
  const location = useLocation()

  return <ConnectedOfferPage
    offerId={offerId ?? props.propertyPageOfferId ?? ''}
    offerSlug={offerSlug}
    locationHash={location.hash}
    propertyPageOfferId={props.propertyPageOfferId}
  />
}

export default OfferPageRoute
